/**
 * Prestação de contas desatualizada (0026) no ERP: o aviso com o que mudou na carga depois do
 * envio, a revisão assumida pelo escritório (com justificativa, só para quem tem a permissão),
 * a nova declaração e o reenvio por quem assumiu, e o histórico das versões enviadas.
 *
 * A regra fica na API e no banco: quem pode revisar (`settlement.revision`), quem declara
 * (`canDeclare`), e o bloqueio da conferência e da finalização para quem revisou.
 */
import {
  SETTLEMENT_DECLARATION_CHANGE_LABEL,
  formatDecimalBR,
  formatMoney,
  parseDecimalBR,
  type LoadDetail,
  type SettlementDeclarationVersion,
} from "@cafegestor/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { DecimalInput } from "@/components/DecimalInput";
import { Alert, Badge, Button } from "@/components/ui";
import { ApiRequestError, apiFetch, errorMessage, newIdempotencyKey } from "@/lib/api";
import { notifySuccess } from "@/lib/notice";
import { LOAD_KEY } from "./lib";

const dateTime = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "America/Sao_Paulo",
  });

const REVISION_MIN = 10;

function useStore(id: string) {
  const queryClient = useQueryClient();
  return async (l: LoadDetail) => {
    queryClient.setQueryData([...LOAD_KEY, "detalhe", id], l);
    await queryClient.invalidateQueries({ queryKey: [...LOAD_KEY, "lista"] });
  };
}

/** Mostra o que for preciso: nada quando a prestação está em dia e nunca foi revisada. */
export function PrestacaoRevisao({ l }: { l: LoadDetail }) {
  const s = l.settlement;
  const inRevision =
    s.status === "rascunho" &&
    s.origin === "vendedor" &&
    s.declaredAt === null &&
    s.declarationVersions.length > 0;
  if (!s.declarationStale && !inRevision && s.declarationVersions.length === 0) return null;
  return (
    <div className="space-y-4">
      {s.declarationStale && <StaleNotice l={l} />}
      {inRevision && <InRevision l={l} />}
      {s.officeReview && !inRevision && s.status === "rascunho" && (
        <Alert tone="info">
          Prestação de contas revisada pelo escritório
          {s.officeReview.byName ? ` (${s.officeReview.byName})` : ""}: “{s.officeReview.reason}”. A
          finalização exige liberação de divergência, e quem revisou não confere nem finaliza.
        </Alert>
      )}
      {s.declarationVersions.length > 0 && <Versions versions={s.declarationVersions} />}
    </div>
  );
}

function StaleNotice({ l }: { l: LoadDetail }) {
  const s = l.settlement;
  const store = useStore(l.id);
  const [reason, setReason] = useState("");
  const [key, setKey] = useState(newIdempotencyKey);
  const inFlight = useRef(false);
  const revise = useMutation({
    mutationFn: () =>
      apiFetch<LoadDetail>(`/cargas/${l.id}/acerto/revisar`, {
        method: "POST",
        idempotencyKey: key,
        body: {
          version: s.version,
          reason: reason.trim() === "" ? null : reason.trim(),
        },
      }),
    onSuccess: async (r) => {
      setKey(newIdempotencyKey());
      setReason("");
      await store(r);
      notifySuccess("Prestação de contas reaberta para revisão. A versão enviada ficou guardada.");
    },
    onError: (e) => {
      if (e instanceof ApiRequestError && e.status < 500) setKey(newIdempotencyKey());
    },
  });
  const office = s.revision?.as === "escritorio";
  const reasonOk = !office || reason.trim().length >= REVISION_MIN;
  const submit = () => {
    if (inFlight.current) return;
    inFlight.current = true;
    revise.mutate(undefined, { onSettled: () => (inFlight.current = false) });
  };

  return (
    <section
      aria-label="Prestação de contas desatualizada"
      className="space-y-3 rounded-2xl border-2 border-warning bg-warning-soft p-5"
    >
      <h3 className="text-[17px] font-semibold">Prestação de contas desatualizada</h3>
      <p className="text-sm">
        A carga mudou depois do envio{s.declaredAt ? ` (${dateTime(s.declaredAt)})` : ""}. A
        conferência e a finalização ficam bloqueadas até a revisão — pelo vendedor da carga ou, com
        justificativa, pelo escritório autorizado.
      </p>
      {s.declarationChanges.length > 0 && (
        <table aria-label="O que mudou depois do envio" className="w-full text-sm">
          <tbody>
            {s.declarationChanges.map((c) => (
              <tr key={`${c.kind}-${c.number}-${c.at}`} className="border-t border-warning/30">
                <td className="py-1.5 pr-3">{SETTLEMENT_DECLARATION_CHANGE_LABEL[c.kind]}</td>
                <td className="py-1.5 pr-3 font-medium">{c.number}</td>
                <td className="py-1.5 pr-3 text-right">
                  {c.amount !== null ? formatMoney(c.amount) : ""}
                </td>
                <td className="py-1.5 text-right text-muted-foreground">{dateTime(c.at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {s.revision?.as === null && s.revision.blockedReason && (
        <p className="text-sm text-muted-foreground">{s.revision.blockedReason}</p>
      )}
      {office && (
        <div className="space-y-2 rounded-xl bg-card p-4">
          <label htmlFor="motivo-revisao" className="font-semibold">
            Assumir a revisão pelo escritório{" "}
            <span className="text-destructive">(justificativa obrigatória)</span>
          </label>
          <textarea
            id="motivo-revisao"
            rows={3}
            maxLength={1000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Por que o escritório revisa no lugar do vendedor? Ex.: vendedor sem sinal na rota."
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-[15px]"
          />
          <p className="text-xs text-muted-foreground">
            Pelo menos {REVISION_MIN} caracteres ({reason.trim().length}). Fica no histórico com o
            seu nome. Quem revisa não informa o retorno, não confere e não finaliza este acerto.
          </p>
          {revise.error && <Alert tone="error">{errorMessage(revise.error)}</Alert>}
          <Button disabled={!reasonOk || revise.isPending} onClick={submit}>
            {revise.isPending ? "Abrindo..." : "Assumir a revisão"}
          </Button>
        </div>
      )}
      {s.revision?.as === "vendedor" && (
        <div className="space-y-2">
          {revise.error && <Alert tone="error">{errorMessage(revise.error)}</Alert>}
          <Button disabled={revise.isPending} onClick={submit}>
            {revise.isPending ? "Abrindo..." : "Revisar prestação de contas"}
          </Button>
        </div>
      )}
    </section>
  );
}

/** Prestação reaberta: quem assumiu declara e reenvia; os demais veem quem está revisando. */
function InRevision({ l }: { l: LoadDetail }) {
  const s = l.settlement;
  if (!l.canDeclare) {
    return <Alert tone="info">{s.nextAction.message}</Alert>;
  }
  return <DeclarationForm l={l} />;
}

function DeclarationForm({ l }: { l: LoadDetail }) {
  const s = l.settlement;
  const store = useStore(l.id);
  const previous = s.declarationVersions[s.declarationVersions.length - 1] ?? null;
  const cashLines = s.payments.filter((p) => p.handling === "em_maos");
  const [counts, setCounts] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      s.products.map((p) => [p.productId, p.declared !== null ? formatDecimalBR(p.declared) : ""]),
    ),
  );
  const [cash, setCash] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      cashLines.map((p) => [
        p.paymentMethodId,
        p.declared !== null ? formatDecimalBR(p.declared, 2) : "",
      ]),
    ),
  );
  const [key, setKey] = useState(newIdempotencyKey);
  const inFlight = useRef(false);
  const send = useMutation({
    mutationFn: async () => {
      const saved = await apiFetch<LoadDetail>(`/cargas/${l.id}/acerto/declaracao`, {
        method: "PUT",
        body: {
          version: s.version,
          notes: null,
          counts: s.products.map((p) => ({
            productId: p.productId,
            counted: parseDecimalBR(counts[p.productId] ?? "", "numero"),
          })),
          payments: cashLines.map((p) => ({
            paymentMethodId: p.paymentMethodId,
            declared: parseDecimalBR(cash[p.paymentMethodId] ?? "", "dinheiro"),
          })),
        },
      });
      return apiFetch<LoadDetail>(`/cargas/${l.id}/acerto/enviar`, {
        method: "POST",
        idempotencyKey: key,
        // o resumo da carga que estava na tela: se ela mudou, a API recusa
        body: { version: saved.settlement.version, basis: s.basis ?? undefined },
      });
    },
    onSuccess: async (r) => {
      setKey(newIdempotencyKey());
      await store(r);
      notifySuccess("Prestação de contas revisada e reenviada.");
    },
    onError: async (e) => {
      if (e instanceof ApiRequestError && e.status < 500) setKey(newIdempotencyKey());
      // a carga pode ter mudado: recarrega para mostrar o atual
      const fresh = await apiFetch<LoadDetail>(`/cargas/${l.id}`).catch(() => null);
      if (fresh) await store(fresh);
    },
  });
  const invalid =
    s.products.some((p) => parseDecimalBR(counts[p.productId] ?? "", "numero") === null) ||
    cashLines.some((p) => parseDecimalBR(cash[p.paymentMethodId] ?? "", "dinheiro") === null);
  const submit = () => {
    if (inFlight.current) return;
    inFlight.current = true;
    send.mutate(undefined, { onSettled: () => (inFlight.current = false) });
  };
  const before = (productId: string) =>
    previous?.counts.find((c) => c.productId === productId && c.lotId === null)?.counted ?? null;

  return (
    <section
      aria-label="Revisão da prestação de contas"
      className="space-y-4 rounded-2xl border-2 border-caramel bg-card p-5"
    >
      <div className="space-y-1">
        <h3 className="text-[17px] font-semibold">Revisão da prestação de contas</h3>
        <p className="text-sm text-muted-foreground">
          Informe a contagem e o dinheiro em mãos com a carga atual e reenvie. O enviado antes
          aparece como referência e fica guardado no histórico.
        </p>
      </div>
      <div className="space-y-3">
        {s.products.map((p) => (
          <label key={p.productId} className="grid gap-1 sm:grid-cols-[1fr_12rem] sm:items-center">
            <span>
              <span className="block font-medium">{p.productName}</span>
              <span className="block text-xs text-muted-foreground">
                Deveria estar com o vendedor: {formatDecimalBR(p.remaining)} {p.baseUnitCode}
                {before(p.productId) !== null &&
                  ` · enviado antes: ${formatDecimalBR(before(p.productId))}`}
              </span>
            </span>
            <DecimalInput
              mode="numero"
              aria-label={`Contagem de ${p.productName}`}
              value={counts[p.productId] ?? ""}
              onChange={(e) => setCounts({ ...counts, [p.productId]: e.target.value })}
            />
          </label>
        ))}
        {cashLines.map((p) => (
          <label
            key={p.paymentMethodId}
            className="grid gap-1 sm:grid-cols-[1fr_12rem] sm:items-center"
          >
            <span>
              <span className="block font-medium">{p.paymentMethodName} em mãos</span>
              <span className="block text-xs text-muted-foreground">
                Esperado pelas vendas: {formatMoney(p.expected)}
              </span>
            </span>
            <DecimalInput
              aria-label={`Valor em ${p.paymentMethodName}`}
              value={cash[p.paymentMethodId] ?? ""}
              onChange={(e) => setCash({ ...cash, [p.paymentMethodId]: e.target.value })}
            />
          </label>
        ))}
      </div>
      {send.error && <Alert tone="error">{errorMessage(send.error)}</Alert>}
      <Button disabled={invalid || send.isPending} onClick={submit}>
        {send.isPending ? "Reenviando..." : "Reenviar prestação de contas"}
      </Button>
    </section>
  );
}

function Versions({ versions }: { versions: SettlementDeclarationVersion[] }) {
  return (
    <details className="rounded-2xl border bg-card p-5">
      <summary className="cursor-pointer font-semibold">
        Versões anteriores da prestação de contas ({versions.length})
      </summary>
      <ol className="mt-4 space-y-4">
        {[...versions].reverse().map((v) => (
          <li key={v.id} className="space-y-2 border-t pt-3 first:border-t-0 first:pt-0">
            <p className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-semibold">Versão {v.round}</span>
              <span className="text-muted-foreground">
                enviada em {dateTime(v.declaredAt)}
                {v.declaredByName ? ` por ${v.declaredByName}` : ""}
              </span>
              {v.basisReference === "reconstituida" && (
                <Badge tone="neutral">referência reconstituída pela data do envio</Badge>
              )}
            </p>
            <ul className="text-sm">
              {v.counts.map((c) => (
                <li key={`${c.productId}-${c.lotId ?? ""}`}>
                  {c.productName}
                  {c.lotCode ? ` (lote ${c.lotCode})` : ""}: {formatDecimalBR(c.counted)}
                </li>
              ))}
              {v.payments.map((p) => (
                <li key={p.paymentMethodId}>
                  {p.paymentMethodName}: {formatMoney(p.declared)} (esperado no envio{" "}
                  {formatMoney(p.expected)})
                </li>
              ))}
            </ul>
            <p className="text-sm">
              Reaberta em {dateTime(v.reopenedAt)}
              {v.reopenedByName ? ` por ${v.reopenedByName}` : ""} (
              {v.reopenKind === "escritorio" ? "escritório" : "vendedor"})
              {v.reopenReason ? ` — “${v.reopenReason}”` : ""}
            </p>
            {v.changes.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Mudou depois do envio:{" "}
                {v.changes
                  .map((c) => `${SETTLEMENT_DECLARATION_CHANGE_LABEL[c.kind]} ${c.number}`)
                  .join(", ")}
              </p>
            )}
          </li>
        ))}
      </ol>
    </details>
  );
}
